"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAppBaseConfigCompositionArtifactContractIssues = getAppBaseConfigCompositionArtifactContractIssues;
exports.assertAppBaseConfigCompositionArtifactContract = assertAppBaseConfigCompositionArtifactContract;
const app_base_contract_1 = require("./app-base-contract");
const app_page_access_1 = require("./app-page-access");
const app_page_exposure_1 = require("./app-page-exposure");
/**
 * The contract between the composition of an App Container and its compiled
 * artifact. It reads the page rights as well, so it lives apart from the
 * config schema that the page exposure builds on.
 */
function getAppBaseConfigCompositionArtifactContractIssues(appBaseConfig) {
    if (appBaseConfig.productMode !== 'app-base')
        return [];
    const issues = [];
    const composition = appBaseConfig.appComposition;
    const manifest = appBaseConfig.appBase;
    const artifact = appBaseConfig.appArtifact;
    if (!composition) {
        issues.push('appComposition is required for Apps');
    }
    if (!manifest) {
        issues.push('appBase manifest is required for Apps');
    }
    if (!artifact) {
        issues.push('appArtifact is required for Apps');
    }
    if (!composition || !manifest || !artifact)
        return issues;
    if (!artifact.provenance) {
        issues.push('appArtifact requires composition compiler provenance');
    }
    else {
        if (artifact.provenance.compositionId !== composition.compositionId) {
            issues.push(`appArtifact provenance compositionId ${artifact.provenance.compositionId} does not match appComposition ${composition.compositionId}`);
        }
        if (artifact.provenance.compositionRevision !== composition.revision) {
            issues.push(`appArtifact provenance revision ${artifact.provenance.compositionRevision} does not match appComposition revision ${composition.revision}`);
        }
    }
    if (manifest.manifestId !== composition.shell.manifestId) {
        issues.push(`appBase manifest manifestId ${manifest.manifestId} does not match composition shell manifestId ${composition.shell.manifestId}`);
    }
    if (manifest.name !== composition.shell.name) {
        issues.push(`appBase manifest name ${manifest.name} does not match composition shell name ${composition.shell.name}`);
    }
    if ((0, app_base_contract_1.stableJsonStringify)(manifest) !== (0, app_base_contract_1.stableJsonStringify)(artifact.manifest)) {
        issues.push('appBase manifest must match appArtifact.manifest');
    }
    if (composition.mounts.length === 0) {
        const compiledArtifact = (0, app_base_contract_1.compileAppComposition)({
            composition,
            artifactId: artifact.artifactId,
            now: artifact.createdAt,
        });
        const compiledShell = app_base_contract_1.AppArtifactSchema.parse({
            ...compiledArtifact,
            manifest: (0, app_base_contract_1.withAppBaseInteractionContract)(compiledArtifact.manifest),
        });
        // Delivery, cache rules and SEO of the shell follow the page rights.
        const expectedArtifact = (0, app_page_exposure_1.withAppPageExposures)({
            ...appBaseConfig,
            appBase: compiledShell.manifest,
            appArtifact: compiledShell,
        }).appArtifact;
        if ((0, app_base_contract_1.stableJsonStringify)(artifact.manifest) !==
            (0, app_base_contract_1.stableJsonStringify)(expectedArtifact.manifest)) {
            issues.push('appArtifact manifest does not match compiled shell');
        }
        if ((0, app_base_contract_1.stableJsonStringify)(artifact.routeModules) !==
            (0, app_base_contract_1.stableJsonStringify)(expectedArtifact.routeModules)) {
            issues.push('appArtifact routeModules do not match compiled shell');
        }
        // Storage replaces inline text with an immutable blob reference. The
        // compiler contract describes content, not that transport representation.
        const fileKeys = Object.keys(artifact.files);
        const expectedFiles = expectedArtifact.files;
        const filesMatch = fileKeys.length === Object.keys(expectedFiles).length &&
            fileKeys.every(key => {
                const actual = artifact.files[key];
                const expected = expectedFiles[key];
                return (expected !== undefined &&
                    actual.path === expected.path &&
                    actual.contentType === expected.contentType &&
                    actual.sha256 === expected.sha256 &&
                    actual.bytes === expected.bytes &&
                    (actual.text !== undefined
                        ? actual.text === expected.text
                        : Boolean(actual.storageKey)));
            });
        if (!filesMatch) {
            issues.push('appArtifact files do not match compiled shell');
        }
    }
    else {
        const hasRootRouteMount = (0, app_base_contract_1.appCompositionHasRootApp)(composition);
        const mountIds = new Set(composition.mounts.map(mount => mount.mountId));
        const ownerMatchesComposition = (ownerId) => {
            if (!hasRootRouteMount && ownerId === 'shell')
                return true;
            return [...mountIds].some(mountId => ownerId === mountId || ownerId.startsWith(`${mountId}:`));
        };
        for (const routeModule of artifact.routeModules) {
            if (ownerMatchesComposition(routeModule.id))
                continue;
            issues.push(`appArtifact routeModule ${routeModule.id} is not owned by appComposition`);
        }
    }
    // The page rights an App brings keep the rules of the access section.
    for (const definition of Object.values(appBaseConfig.appDefinitions)) {
        for (const issue of (0, app_page_access_1.appDefinitionAccessIssues)(definition)) {
            issues.push(`App Module ${definition.appId} page rights break ${(0, app_page_access_1.formatAppPageAccessIssue)(issue)}`);
        }
    }
    return issues;
}
function assertAppBaseConfigCompositionArtifactContract(appBaseConfig) {
    const issues = getAppBaseConfigCompositionArtifactContractIssues(appBaseConfig);
    if (issues.length === 0)
        return;
    throw new Error(`App composition/artifact contract violated: ${issues.join('; ')}`);
}
